import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { formatUsageValue } from "@/utils/formatters";
const DONUT_COLORS = [
    "#22d3ee",
    "#84cc16",
    "#a78bfa",
    "#f59e0b",
    "#f43f5e",
    "#14b8a6",
    "#3b82f6",
    "#ec4899",
    "#f97316",
    "#06b6d4",
    "#64748b"
];
function shortApiKey(apiKeyId) {
    return apiKeyId.length > 14 ? `${apiKeyId.slice(0, 14)}...` : apiKeyId;
}
function buildConicGradient(segments) {
    if (segments.length === 0) {
        return "conic-gradient(#e2e8f0 0deg 360deg)";
    }
    let current = 0;
    const stops = segments.map((segment)=>{
        const start = current;
        const end = current + segment.percentage * 3.6;
        current = end;
        return `${segment.color} ${start}deg ${end}deg`;
    });
    if (current < 360) {
        stops.push(`#e2e8f0 ${current}deg 360deg`);
    }
    return `conic-gradient(${stops.join(", ")})`;
}
export function UsagePieChart({ data, apiKeys }) {
    const { totalUsageValue, usageUnit } = data;
    const keyNameMap = useMemo(()=>{
        const map = new Map();
        apiKeys.forEach((apiKey)=>{
            if (apiKey.name) {
                map.set(apiKey.id, apiKey.name);
            }
        });
        return map;
    }, [
        apiKeys
    ]);
    const keyUsageTotals = useMemo(()=>data.apiKeyTotals.filter((item)=>item.apiKeyId), [
        data.apiKeyTotals
    ]);
    const topTenKeys = useMemo(()=>{
        return [
            ...keyUsageTotals
        ].sort((left, right)=>right.totalUsageValue - left.totalUsageValue).slice(0, 10);
    }, [
        keyUsageTotals
    ]);
    const donutSegments = useMemo(()=>{
        if (totalUsageValue <= 0 || topTenKeys.length === 0) {
            return [];
        }
        const topSegments = topTenKeys.slice(0, 10).map((item, index)=>({
                label: keyNameMap.get(item.apiKeyId) || shortApiKey(item.apiKeyId),
                value: item.totalUsageValue,
                percentage: item.totalUsageValue / totalUsageValue * 100,
                color: DONUT_COLORS[index % DONUT_COLORS.length]
            }));
        const usedValue = topSegments.reduce((sum, segment)=>sum + segment.value, 0);
        const remainingValue = Math.max(totalUsageValue - usedValue, 0);
        if (remainingValue > 0) {
            topSegments.push({
                label: "Others",
                value: remainingValue,
                percentage: remainingValue / totalUsageValue * 100,
                color: DONUT_COLORS[DONUT_COLORS.length - 1]
            });
        }
        return topSegments;
    }, [
        keyNameMap,
        topTenKeys,
        totalUsageValue
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "bg-white shadow rounded-lg",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "px-6 py-4 border-b border-gray-200",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap items-center justify-between gap-2",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-lg font-medium text-gray-900",
                                    children: "Consumption Breakdown"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "mt-1 text-sm text-gray-500",
                                    children: "Top API keys by usage for the selected period"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("span", {
                            className: "rounded-lg border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-500",
                            children: [
                                apiKeys.length.toLocaleString(),
                                " total keys"
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "p-6",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-1 gap-6 xl:grid-cols-[320px_1fr]",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "flex items-center justify-center",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: "relative h-72 w-72 rounded-full",
                                style: {
                                    background: buildConicGradient(donutSegments)
                                },
                                children: /*#__PURE__*/ _jsxs("div", {
                                    className: "absolute inset-14 flex flex-col items-center justify-center rounded-full border border-gray-200 bg-white",
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-2xl font-semibold text-gray-900",
                                            children: formatUsageValue(totalUsageValue, usageUnit)
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "mt-1 text-xs text-gray-500",
                                            children: [
                                                keyUsageTotals.length.toLocaleString(),
                                                " keys in period"
                                            ]
                                        })
                                    ]
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-3",
                            children: [
                                topTenKeys.length === 0 && /*#__PURE__*/ _jsx("div", {
                                    className: "rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500",
                                    children: "No API key usage was recorded in this period."
                                }),
                                topTenKeys.map((item, index)=>{
                                    const usagePercent = totalUsageValue > 0 ? item.totalUsageValue / totalUsageValue * 100 : 0;
                                    const label = keyNameMap.get(item.apiKeyId) || shortApiKey(item.apiKeyId);
                                    return /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center justify-between text-sm",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("p", {
                                                        className: "truncate pr-4 text-gray-700",
                                                        children: [
                                                            index + 1,
                                                            ". ",
                                                            label
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx("p", {
                                                        className: "text-gray-500",
                                                        children: formatUsageValue(item.totalUsageValue, usageUnit)
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "h-2 overflow-hidden rounded bg-gray-100",
                                                children: /*#__PURE__*/ _jsx("div", {
                                                    className: "h-full rounded",
                                                    style: {
                                                        width: `${Math.max(usagePercent, 1)}%`,
                                                        backgroundColor: DONUT_COLORS[index % DONUT_COLORS.length]
                                                    }
                                                })
                                            }),
                                            /*#__PURE__*/ _jsxs("p", {
                                                className: "text-right text-xs text-gray-500",
                                                children: [
                                                    usagePercent.toFixed(1),
                                                    "%"
                                                ]
                                            })
                                        ]
                                    }, item.apiKeyId);
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
